import { AppRegistry } from "Apps/AppRegistry";
import { useSearchContact } from "Apps/Contacts/useSearchContact";
import { useIncidentUniversalSearch } from "Apps/Incident/Hooks/useIncidentUniversalSearch";
import { useRulesUniversalSearch } from "Apps/Rules/Hooks/useRulesUniversalSearch";
import { useSearchService } from "Apps/Service/Components/useSearchService";
import { useServerUniversalSearch } from "Apps/Server/Hooks/useServerUniversalSearch";
import { TeamAppModel } from "Apps/TeamAppModel";
import { useSearchTeams } from "Apps/Teams/useSearchTeams";
import { RenderingStatus } from "Enumerations";
import { observer } from "mobx-react";
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { useUniversalSearch } from "Shared/Hooks";
import { useRootStore } from "Store/RootStore";
import { useTenantUniversalSearch } from "Apps/CustomerHub/Hooks/useTenantUniversalSearch";
import { useServiceHubPeopleSearch } from "Apps/Service/Components/useServicePeopleUniversalSearch";
import { useRuntimeInsightsUniversalSearch } from "Apps/RuntimeInsights/L3/useRuntimeInsightsUniversalSearch";

const matchesAppPath = (pathname: string, appPath?: string): boolean => {
    if (!appPath) {
        return false;
    }
    const normalizedPath = pathname.replace(/\/+$/, "").toLowerCase() || "/";
    const normalizedBase = appPath.replace(/\/+$/, "").toLowerCase() || "/";

    if (normalizedBase === "/") {
        return normalizedPath.startsWith("/");
    }
    return normalizedPath === normalizedBase || normalizedPath.startsWith(`${normalizedBase}/`);
};

const SearchGlobalBase = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const appStore = useRootStore("AppSettingStore");
    const authStore = useRootStore("AuthStore");
    const searchStore = useRootStore("UniversalSearchStore");

    useEffect(() => {
        searchStore.searchWord = "";
        searchStore.setFilter(null);
    }, [location.pathname, searchStore]);

    const searchResource = useCallback(
        (word: string) => {
            const searchResult: TeamAppModel[] = [];
            appStore.appModels.forEach((app) => {
                const textToSearch = [app.title, app.tags.join(" "), app.path].join(" ").toLowerCase();
                if (!app.hiddenFromSearch && word !== "" && textToSearch.includes(word.trim().toLowerCase())) {
                    searchResult.push(app);
                }
            });
            const resultList = searchResult.map((item) => ({
                id: item.id,
                title: item.title,
                description: item.tags.length > 0 ? item.tags.join(" ") : item.path,
                icon: item.filledIcon,
                onClick: () => navigate(item.path),
                navigationTarget: item.path
            }));
            return Promise.resolve(resultList);
        },
        [appStore.appModels, navigate]
    );
    useUniversalSearch("Apps", "apps", searchResource, false, AppRegistry.AppHub.path);
    useSearchContact();
    useSearchService();
    useSearchTeams(authStore.loginStatus === RenderingStatus.Ready);
    useIncidentUniversalSearch();
    useServerUniversalSearch();
    useRulesUniversalSearch();
    useTenantUniversalSearch();
    useServiceHubPeopleSearch();
    useRuntimeInsightsUniversalSearch();

    const subscriptionCount = searchStore.subscriptionMap.size;
    useEffect(() => {
        const match = Array.from(searchStore.subscriptionMap.entries())
            .filter(([, sub]) => matchesAppPath(location.pathname, sub.appPath))
            .sort((a, b) => (b[1].appPath?.length ?? 0) - (a[1].appPath?.length ?? 0))[0];
        searchStore.setContextCategory(match?.[0]);
    }, [location.pathname, searchStore, subscriptionCount]);

    return null;
};

export const SearchGlobal = observer(SearchGlobalBase);
